/*
 * aos-refresh.js
 *
 * AOS stale-trigger repair (root cause).
 * Spec: live-theme\a11y-workstream\solution-drafts\aos-failsafe\SPEC.md
 *
 * Problem, measured 6/6 loads. assets/theme.js calls AOS.init() at readyState
 * "interactive". The bundled AOS 2.3 (assets/vendor.js:1296) computes every
 * element's trigger position at that moment, and again at DOMContentLoaded, and
 * never afterwards: the library has no window.load refresh. On a PDP the media
 * gallery is still an uncollapsed image stack at that point, and Flickity later
 * collapses roughly 1000px of page height, so every stored trigger ends up
 * about 900 to 1300px lower than it should be. Elements reveal far too late,
 * and an element whose stale trigger sits past the page's maximum scroll never
 * receives .aos-animate at all, which leaves it at opacity: 0 permanently
 * (theme.css:1102). The CSS failsafe in assets/custom-css.css catches that last
 * case; this file removes the cause.
 *
 * Route. AOS registers its own debounced refresh on the window resize and
 * orientationchange events (vendor.js:1296,
 * window.addEventListener("resize", debounce(refresh, 50, true))), and its
 * refresh() recomputes every position and immediately re-runs the scroll
 * handler. window.AOS is not exposed as a global in this bundle, so dispatching
 * a synthetic resize is the least novel way in: it is the same code path the
 * browser exercises on every real resize, address-bar collapse and rotation.
 * The other route found during diagnosis, dispatching shopify:section:load to
 * reach theme.js's own AOS.refresh() handler, also works but re-runs
 * floatLabels, moveModals and preventOverflow as side effects, so it is not
 * used.
 *
 * Additive and isolated: assets/theme.js and assets/vendor.js are minified
 * bundles with no source in this repo and are not touched. Loaded from
 * layout/theme.liquid alongside atc-guard.js, predictive-search-dedup.js and
 * coded-price.js, which is this theme's established pattern for custom scripts.
 * assets/custom.js is NOT that pattern: its script tag sits inside a Liquid
 * comment (theme.liquid:268-272), so that file has never been served.
 *
 * Fires once after load, then re-checks while the document height is still
 * settling (lazy images, app embeds). Bounded: it stops after
 * MAX_REFRESHES dispatches and WATCH_MS after load, and only re-dispatches when
 * the height actually moved, so it cannot loop on its own resize.
 *
 * Second job (AMENDMENT 3): mark what the CSS failsafe already revealed.
 * Once the failsafe reveal has run, a later .aos-animate would restart heroFade
 * from opacity 0 with animation-fill-mode: both and up to 2250ms of
 * [data-aos-order] stagger delay (theme.css:1098-1114), so content that has been
 * on screen for seconds blinks out and fades back in. Reproduced on the QA
 * theme: dwell past ~5.4s, then scroll, and the revealed section titles
 * disappear for up to ~2.25s. The listener below stamps every element the
 * failsafe actually revealed with .tc-aos-revealed, and assets/custom-css.css
 * pins those elements to their final state so .aos-animate cannot re-animate
 * them. Elements that animated normally never get the mark and keep their full
 * animation.
 */
(function () {
  var MAX_REFRESHES = 8;
  var WATCH_MS = 6000;
  var SETTLE_MS = 150;
  var EXTRA_CHECKS = [400, 1200, 3000];

  var refreshes = 0;
  var lastHeight = 0;
  var settleTimer = null;
  var observer = null;

  function documentHeight() {
    return document.documentElement.scrollHeight;
  }

  function heightMoved() {
    var height = documentHeight();
    if (Math.abs(height - lastHeight) < 2) return false;
    lastHeight = height;
    return true;
  }

  function refreshAos() {
    if (refreshes >= MAX_REFRESHES) return;
    refreshes++;
    window.dispatchEvent(new Event('resize'));
  }

  function stopWatching() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
  }

  function onBodyResize() {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      settleTimer = null;
      if (heightMoved()) refreshAos();
    }, SETTLE_MS);
  }

  function onLoad() {
    lastHeight = documentHeight();
    refreshAos();

    if (typeof window.ResizeObserver === 'function' && document.body) {
      observer = new ResizeObserver(onBodyResize);
      observer.observe(document.body);
    }
    setTimeout(stopWatching, WATCH_MS);

    // Belt and braces for the paths where the observer never fires.
    EXTRA_CHECKS.forEach(function (delay) {
      setTimeout(function () {
        if (heightMoved()) refreshAos();
      }, delay);
    });
  }

  if (document.readyState === 'complete') {
    onLoad();
  } else {
    window.addEventListener('load', onLoad, { once: true });
  }

  /* ---- AMENDMENT 3: stamp failsafe-revealed elements ----------------------
   *
   * Two animation names can signal a failsafe reveal:
   *
   *   tcAosFailsafeReveal  the opacity failsafe (custom-css.css). Its target is
   *                        the [data-aos] element itself for the hero, fade,
   *                        zoom and img-in families, and the [data-aos-icon]
   *                        child for the icons family.
   *   dash                 the theme's own stroke keyframe, which the failsafe
   *                        re-uses on a 5s delay for the svg-dash and icons
   *                        stroke descendants. Its target is a path, ellipse or
   *                        circle, several levels below the [data-aos] element.
   *
   * closest('[data-aos]') normalises all of those to the element the CSS keys
   * off. closest() is defined on SVGElement as well as HTMLElement, so the
   * stroke targets resolve the same way.
   *
   * The .aos-animate guard. "dash" is ALSO the name of the normal, non-failsafe
   * stroke animation (theme.css:1059 and 1194-1197), so a page that animated
   * normally fires this listener too; the guard is what keeps those elements
   * unmarked and fully animated. For the opacity failsafe the guard is
   * belt-and-braces rather than load-bearing: the failsafe rules are gated on
   * :not(.aos-animate), so if .aos-animate lands first the failsafe animation is
   * removed mid-flight and fires animationcancel, never animationend. The only
   * way both could be true is a sub-frame race where .aos-animate is added
   * between the animation finishing and this handler running; the guard settles
   * that deterministically in favour of the real animation, which is the
   * conservative choice (the element is on screen either way).
   *
   * Permanent, not scoped to the load-time watch window: the failsafe fires at
   * 5s after first paint and its stroke variants finish at 6.5s, both of which
   * can land after the watcher has stopped, and app DOM replacement
   * can start a fresh reveal at any time. One capture-phase listener on document
   * costs nothing when no animation is running, and every early return happens
   * before any DOM work.
   */
  var FAILSAFE_ANIMATION = 'tcAosFailsafeReveal';
  var STROKE_ANIMATION = 'dash';

  function isFailsafeName(name) {
    return name === FAILSAFE_ANIMATION || name === STROKE_ANIMATION;
  }

  /* Catch up on reveals that finished before this file arrived.
   *
   * This script is deferred and is requested after vendor.js and theme.js, so on
   * a slow or stalled connection it can execute more than 5.4s (6.5s for the
   * stroke variants) after the elements were parsed and the CSS failsafe started
   * counting. An animationend that has already fired is never replayed, so
   * without this pass those elements would stay unmarked and would still blink
   * when AOS - already loaded and running from theme.js - added .aos-animate on
   * the first scroll. Codex round-3 P2 on PR #233; the failsafe layer exists
   * precisely for the case where a script is late, so it must not itself depend
   * on being early.
   *
   * getAnimations({subtree: true}) reaches the [data-aos-icon] children and the
   * path/ellipse/circle strokes, which is where the failsafe animations actually
   * live for the icons and svg-dash families. It is wrapped because the subtree
   * option is not universal; a browser that lacks it simply keeps the listener
   * behaviour, which is the pre-existing state and still correct for every
   * normally timed load. Elements that already carry .aos-animate are skipped
   * for the same reason as in the listener: their finished "dash" animation is
   * the theme's ordinary one, not a failsafe reveal. */
  function reconcileCompletedReveals() {
    var nodes = document.querySelectorAll('[data-aos]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.classList.contains('aos-animate') || el.classList.contains('tc-aos-revealed')) continue;
      if (typeof el.getAnimations !== 'function') return;

      var anims;
      try {
        anims = el.getAnimations({ subtree: true });
      } catch (e) {
        return;
      }

      for (var j = 0; j < anims.length; j++) {
        if (isFailsafeName(anims[j].animationName) && anims[j].playState === 'finished') {
          el.classList.add('tc-aos-revealed');
          break;
        }
      }
    }
  }

  document.addEventListener(
    'animationend',
    function (event) {
      if (!isFailsafeName(event.animationName)) return;

      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var host = target.closest('[data-aos]');
      if (!host || host.classList.contains('aos-animate')) return;

      host.classList.add('tc-aos-revealed');
    },
    true
  );

  reconcileCompletedReveals();
})();
